import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Spinner } from "@fluentui/react-components";
import { ChecklistGara, Gare, Lotti, ModelloChecklist, Repository, ServiziGara } from "@app/schema";
import { DocumentLibrary, DriveFolders, GraphClient, ListRepository, getSiteByUrl, getTeamSite, type SiteInfo } from "@app/sp-data";
import { graphToken } from "../auth/msal";
import { config } from "../config";
import { useHost } from "../teams/TeamsProvider";
import { ErrorState } from "../components/ErrorState";
import type { CartelleCtx } from "./cartelle";

/**
 * Client Graph condiviso. In modalità demo le chiamate sono servite da un
 * finto Graph in memoria (caricato solo in quel caso).
 */
export const graph = new GraphClient({
  getToken: graphToken,
  fetchImpl: __DEMO__ ? async (input, init) => (await import("../demo/mockGraph")).mockFetch(input, init) : undefined,
});

export interface Me {
  id: string;
  displayName: string;
  userPrincipalName: string;
  mail?: string;
}

export interface Roles {
  /** Crea e modifica gare, lotti e servizi; crea le cartelle. */
  gestore: boolean;
  /** Lavora nelle cartelle e sulle checklist dei documenti; le gare le vede in sola lettura. */
  collaboratore: boolean;
  lettore: boolean;
}

/** Tutto ciò che le pagine usano per leggere/scrivere dati. */
export interface DataServices extends CartelleCtx {
  site: SiteInfo;
  me: Me;
  roles: Roles;
}

const DataContext = createContext<DataServices | undefined>(undefined);

export function useData(): DataServices {
  const v = useContext(DataContext);
  if (!v) throw new Error("useData() fuori da <DataProvider>");
  return v;
}

/**
 * Sito dei dati, in ordine di priorità:
 *  1. VITE_SITE_URL (sito fisso dell'app: la scheda funziona in qualsiasi canale);
 *  2. parametro `siteId` nell'URL (scritto dalla pagina di configurazione);
 *  3. sito del team corrente;
 *  4. VITE_DEV_SITE_URL (sviluppo nel browser).
 */
async function resolveSite(groupId?: string): Promise<SiteInfo> {
  if (config.siteUrl) return getSiteByUrl(graph, config.siteUrl);
  const siteId = new URLSearchParams(window.location.search).get("siteId");
  if (siteId) return graph.request<SiteInfo>(`/sites/${siteId}?$select=id,displayName,webUrl`);
  if (groupId) return getTeamSite(graph, groupId);
  if (config.devSiteUrl) return getSiteByUrl(graph, config.devSiteUrl);
  throw new Error("Nessun sito configurato per questa app.");
}

/**
 * Ruoli dall'appartenenza ai gruppi di sicurezza (solo i MEMBRI contano, non i proprietari).
 * Funziona con qualsiasi licenza e non richiede l'assegnazione dei gruppi all'app.
 */
async function resolveRoles(): Promise<Roles> {
  const g = config.groups;
  const ids = [g.gestori, g.collaboratori, g.lettori].filter((x): x is string => !!x);
  if (!ids.length) return { gestore: true, collaboratore: true, lettore: true }; // nessun gruppo configurato
  const res = await graph.request<{ value: string[] }>("/me/checkMemberGroups", { method: "POST", body: { groupIds: ids } });
  const has = (id?: string) => !!id && res.value.includes(id);
  return { gestore: has(g.gestori), collaboratore: has(g.collaboratori), lettore: has(g.lettori) };
}

export function DataProvider({ children }: { children: ReactNode }) {
  const { groupId } = useHost();
  const [state, setState] = useState<{ site: SiteInfo; me: Me; roles: Roles; driveId: string }>();
  const [error, setError] = useState<unknown>();

  useEffect(() => {
    (async () => {
      const [site, me, roles] = await Promise.all([
        resolveSite(groupId),
        graph.request<Me>("/me?$select=id,displayName,userPrincipalName,mail"),
        resolveRoles(),
      ]);
      const driveId = await new DocumentLibrary(graph, site.id, Repository).drive();
      setState({ site, me, roles, driveId });
    })().catch(setError);
  }, [groupId]);

  const services = useMemo<DataServices | undefined>(
    () =>
      state && {
        site: state.site,
        me: state.me,
        roles: state.roles,
        graph,
        siteId: state.site.id,
        gare: new ListRepository(graph, state.site.id, Gare),
        lotti: new ListRepository(graph, state.site.id, Lotti),
        servizi: new ListRepository(graph, state.site.id, ServiziGara),
        checklist: new ListRepository(graph, state.site.id, ChecklistGara),
        modelloChecklist: new ListRepository(graph, state.site.id, ModelloChecklist),
        repository: new DriveFolders(graph, state.driveId),
        modelloUrl: config.modelloUrl,
        numeroBase: config.numeroBase,
      },
    [state],
  );

  if (error) return <ErrorState error={error} />;
  if (!services) return <Spinner style={{ marginTop: 64 }} label="Caricamento…" />;
  return <DataContext.Provider value={services}>{children}</DataContext.Provider>;
}
